import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";

/** A household with the caller's own place in it (the API sends them as one). */
export interface Household {
  id: string;
  name: string;
  base_currency_code: string;
  plan: string;
  default_locale: string;
  role: "owner" | "member" | "viewer";
  invited_at?: string;
  accepted_at?: string;
  created_at: string;
}

/**
 * The response from `GET /api/v1/me`.
 *
 * It carries the households and the unanswered invitations, which is what lets
 * the app decide in one request what to render: a household, an invitation
 * prompt, or the create-your-first-household screen.
 */
export interface Me {
  subject: string;
  user_id: string;
  email: string;
  email_verified: boolean;
  display_name: string;
  locale: string;
  /** The person's colour theme; see `src/theme/themes.ts`. */
  theme?: string;
  /** Whether the interface follows the device or is pinned light/dark; see `src/theme/themes.ts`. */
  scheme?: string;
  timezone: string;
  households: Household[];
  invitations: Household[];
}

export const meQueryKey = ["me"] as const;

/** Fetches the authenticated caller. Enabled only once signed in. */
export function useMe(enabled: boolean) {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: ({ signal }) => apiFetch<Me>("/api/v1/me", { signal }),
    enabled,
  });
}

export interface CreateHouseholdInput {
  name: string;
  base_currency_code: string;
}

/**
 * Creates a household and makes the caller its owner.
 *
 * `me` is invalidated rather than patched: the server decides the role, the plan
 * and the id, and a client that guessed any of them would be showing a household
 * that does not quite exist.
 */
export function useCreateHousehold() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateHouseholdInput) =>
      apiFetch<Household>("/api/v1/households", { method: "POST", body: input }),
    onSuccess: () => client.invalidateQueries({ queryKey: meQueryKey }),
  });
}
